/**
 * Product Hero Banner — Custom Element
 *
 * 视频懒加载：
 * - IntersectionObserver 监听模块进入/离开视口
 * - 进入：加载 src → play()
 * - 离开：pause()
 * - click-to-play 模式：点击切换播放/暂停
 * - prefers-reduced-motion 下不自动播放
 */
class ProductHeroBanner extends HTMLElement {
  constructor() {
    super();
    this.videoMode = this.dataset.videoMode || 'auto';
    this.hasDesktopVideo = this.dataset.hasDesktopVideo === 'true';
    this.hasMobileVideo = this.dataset.hasMobileVideo === 'true';
    this._videoLoaded = false;
    this._isVisible = false;
    this._prefersReducedMotion = false;
    this._observer = null;
    this._motionQuery = null;
    this._boundOnIntersect = this._onIntersect.bind(this);
    this._boundOnClick = this._onClickPlay.bind(this);
    this._boundOnMotionChange = this._onMotionChange.bind(this);
  }

  connectedCallback() {
    if (!this.hasDesktopVideo && !this.hasMobileVideo) return;

    this._checkReducedMotion();

    if (this.videoMode === 'click') {
      this._bindClickToPlay();
    }

    // requestAnimationFrame 确保 DOM 已完成初始布局后再绑定 observer
    var self = this;
    requestAnimationFrame(function () {
      self._setupObserver();
    });
  }

  disconnectedCallback() {
    this._teardownObserver();
    if (this._motionQuery) {
      this._motionQuery.removeEventListener('change', this._boundOnMotionChange);
      this._motionQuery = null;
    }
    this._unbindClickToPlay();
    this._pauseAllVideos();
  }

  /* ---------- reduced motion ---------- */
  _checkReducedMotion() {
    this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._prefersReducedMotion = this._motionQuery.matches;
    this._motionQuery.addEventListener('change', this._boundOnMotionChange);
  }

  _onMotionChange() {
    this._prefersReducedMotion = this._motionQuery.matches;
    if (this._prefersReducedMotion) {
      this._pauseAllVideos();
    } else if (this._isVisible && this.videoMode === 'auto') {
      this._loadAndPlayCurrentVideo();
    }
  }

  /* ---------- IntersectionObserver ---------- */
  _setupObserver() {
    if (this._observer) return;
    var self = this;
    this._observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self._boundOnIntersect(entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '200px 0px' }
    );
    this._observer.observe(this);
  }

  _teardownObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _onIntersect(isIntersecting) {
    if (isIntersecting && !this._isVisible) {
      this._isVisible = true;
      this._onVisible();
    } else if (!isIntersecting && this._isVisible) {
      this._isVisible = false;
      this._onHidden();
    }
  }

  _onVisible() {
    if (this.videoMode === 'auto' && !this._prefersReducedMotion) {
      this._loadAndPlayCurrentVideo();
    }
  }

  _onHidden() {
    this._pauseCurrentVideo();
  }

  /* ---------- 获取当前断点视频 ---------- */
  _getCurrentVideo() {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var variant = isMobile ? 'mobile' : 'desktop';
    return this.querySelector(
      '.product-hero-banner__video[data-media-variant="' + variant + '"]'
    );
  }

  /* ---------- 加载并播放当前视频 ---------- */
  _loadAndPlayCurrentVideo() {
    var video = this._getCurrentVideo();
    if (!video) return;

    // 从 data-src 赋值到 src
    var sources = video.querySelectorAll('source[data-src]');
    var needsLoad = false;
    sources.forEach(function (source) {
      if (source.dataset.src && !source.src) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        needsLoad = true;
      }
    });

    if (needsLoad) {
      video.load();
    }

    // 立即尝试播放（不等 loadeddata 事件）
    if (video.paused) {
      var promise = video.play();
      if (promise && promise.catch) {
        promise.catch(function () {
          // 浏览器可能因用户未交互而阻止自动播放，静默
        });
      }
    }

    this._videoLoaded = true;
  }

  _pauseCurrentVideo() {
    var video = this._getCurrentVideo();
    if (video && !video.paused) {
      video.pause();
    }
  }

  _pauseAllVideos() {
    var videos = this.querySelectorAll('.product-hero-banner__video');
    videos.forEach(function (v) {
      if (!v.paused) v.pause();
    });
  }

  /* ---------- click-to-play ---------- */
  _bindClickToPlay() {
    var wrapper = this.querySelector('.product-hero-banner__media-wrapper');
    var btn = this.querySelector('[data-play-btn]');
    if (wrapper) wrapper.addEventListener('click', this._boundOnClick);
    if (btn) btn.addEventListener('click', this._boundOnClick);
  }

  _unbindClickToPlay() {
    var wrapper = this.querySelector('.product-hero-banner__media-wrapper');
    var btn = this.querySelector('[data-play-btn]');
    if (wrapper) wrapper.removeEventListener('click', this._boundOnClick);
    if (btn) btn.removeEventListener('click', this._boundOnClick);
  }

  _onClickPlay() {
    var self = this;

    // 首次点击时加载视频
    if (!self._videoLoaded) {
      var video = self._getCurrentVideo();
      if (video) {
        var sources = video.querySelectorAll('source[data-src]');
        sources.forEach(function (source) {
          if (source.dataset.src && !source.src) {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
          }
        });
        video.load();
        self._videoLoaded = true;
      }
    }

    var video = self._getCurrentVideo();
    if (!video) return;

    if (video.paused) {
      video.play().catch(function () {});
    } else {
      video.pause();
    }

    self._updatePlayButtonState();
  }

  _updatePlayButtonState() {
    var wrapper = this.querySelector('.product-hero-banner__media-wrapper');
    var video = this._getCurrentVideo();
    if (wrapper && video) {
      if (!video.paused) {
        wrapper.classList.add('is-playing');
      } else {
        wrapper.classList.remove('is-playing');
      }
    }
  }
}

if (!customElements.get('product-hero-banner')) {
  customElements.define('product-hero-banner', ProductHeroBanner);
}
